import axios from "axios";
import Cookie from "universal-cookie";

const HOST = process.env.REACT_APP_SIM_BACKEND_HOST;

export const getCookie = (name: string) => {
  const value = `; ${document.cookie}`;
  console.log("value", value);
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift();
  return null;
};

export async function getTokenOrRefresh() {
  const cookie = new Cookie();
  const speechToken = cookie.get("speech-token");
  const accessToken = localStorage.getItem("accessToken");

  // Check if the token is expired or about to expire (within 30 seconds)
  const isTokenExpired = () => {
    if (!speechToken) return true;
    const [, token] = speechToken.split(':');
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now() + 30000; // 30 seconds buffer
  };

  if (speechToken === undefined || isTokenExpired()) {
    try {
      const res = await axios.post(`${HOST}/sim/refresh`, null, {
        headers: {
          Authorization: "Bearer " + accessToken,
        },
        timeout: 5000, // Set a timeout of 5 seconds
      });
      const token = res.data.az_token;
      const region = res.data.region;
      cookie.set("speech-token", region + ":" + token, {
        maxAge: 100,
        path: "/",
      });

      return { authToken: token, region: region };
    } catch (err: any) {
      console.error("Error refreshing token:", err);
      if (err.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error("Response data:", err.response.data);
        console.error("Response status:", err.response.status);
        console.error("Response headers:", err.response.headers);
      } else if (err.request) {
        // The request was made but no response was received
        console.error("No response received:", err.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error("Error setting up request:", err.message);
      }
      return { authToken: null, error: err };
    }
  } else {
    const idx = speechToken.indexOf(":");
    return {
      authToken: speechToken.slice(idx + 1),
      region: speechToken.slice(0, idx),
    };
  }
}

export async function sendBlobToBackend(url: string, blob: any, text: string, currentResponseTime: number) {
  const accessToken = localStorage.getItem("access_token");
  const formData = new FormData();
  formData.append("audio", blob);
  formData.append("text", text);
  formData.append("current_response_time", currentResponseTime.toString());
  try {
    const res = await axios.post(url, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: "Bearer " + accessToken,
      },
    });
    // console.log("Response:", res.bot_response);
    if (res.status === 401) {
      return res;
    }
    return res.data;

  } catch (error) {
    console.error("Error:", error);
    // Handle the error as needed
  }
}

export async function fetchAuthenticatedUrl(url: string) {
  try {
    let accessToken = localStorage.getItem("access_token");
    if (accessToken === "undefined" || accessToken === null) {
      const token = getCookie("access_token");
      console.log("token", token);
      if (!token) {
        throw new Error("No access token found");
      }
      localStorage.setItem("access_token", token);
      accessToken = token;
    }
    console.log("fetching from...", url);
    console.log("access token", accessToken);
    const response = await fetch(url, {
      headers: new Headers({
        Authorization: "Bearer " + accessToken,
      }),
    });
    if (!response.ok) {
      if (response.status === 401) {
        return response;
      } else {
        throw new Error("Failed to fetch simulation data");
      }
    }
    const resp = await response.json();

    return {
      data: resp.data,
      error: undefined,
    };
  } catch (error) {
    console.log("error", error);
    return {
      data: undefined,
      error: error,
    };
  }
}

export async function postURL(url: string, data: FormData = new FormData()) {
  try {
    const accessToken = localStorage.getItem("accessToken");
    const response = await axios.post(url, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: "Bearer " + accessToken,
      },
    });
    return {
      data: response.data,
      error: undefined,
    };
  } catch (error) {
    return {
      data: undefined,
      error: error,
    };
  }
}

export async function endChat(public_sim_id: string, public_chat_id: string, summary?: string) {
  const accessToken = localStorage.getItem("accessToken");
  const response = await axios.post(`${HOST}/sim/${public_sim_id}/${public_chat_id}/end`, { summary }, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: "Bearer " + accessToken,
    },
  });
  return response;
}

export const setDataInLocalStorage = (key: string, value: string, expiry: number = 48) => {
  const expiryDate = new Date(Date.now() + expiry * 60 * 60 * 1000).toISOString();
  const valueWithExpiry = { value, expiry: expiryDate };
  localStorage.setItem(key, JSON.stringify(valueWithExpiry));
}

export const getDataFromLocalStorage = (key: string) => {
  const valueWithExpiry = JSON.parse(localStorage.getItem(key) || '{}');
  if (valueWithExpiry.expiry && new Date(valueWithExpiry.expiry) < new Date()) {
    localStorage.removeItem(key);
    return null;
  }
  return valueWithExpiry.value;
}
interface RootObject {
  status: string;
  message_count: number;
  response: Response;
}
interface Response {
  empathy: Empathy;
  partnership: Partnership;
  softening_sustain_talk: Softeningsustaintalk;
  cultivating_change_talk: Cultivatingchangetalk;
  voice_qa?: any;
}
interface Cultivatingchangetalk {
  clinician_cct_score: number;
  clinician_cct_better1: string;
  clinician_cct_better2: string;
}
interface Softeningsustaintalk {
  clinician_scct_score: number;
  clinician_scct_better1: string;
  clinician_scct_better2: string;
}
interface Partnership {
  clinician_p_score: number;
  clinician_p_better1: string;
  clinician_p_better2: string;
}
interface Empathy {
  clinician_e_score: number;
  clinician_e_better1: string;
  clinician_e_better2: string;
}

export async function realtimeFeedback(url: string, messages: any, audio: any, sim_type: string, feedbackConfig: string[]): Promise<RootObject> {
  const formData = new FormData();
  formData.append('messages', JSON.stringify(messages));
  formData.append('audio', audio);
  formData.append('sim_type', sim_type);
  formData.append('feedbackConfig', JSON.stringify(feedbackConfig));
  const accessToken = localStorage.getItem("accessToken");
  const response = await axios.post(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      Authorization: "Bearer " + accessToken,
    },
  });
  return response.data;
}



// const keyValuePairs = {
//     'uncover_patients_own_understanding_of_health': "Uncover patient's own understanding of their health",
//     'twelve_conversational_exchanges_complete': "Explore the client's challenges in sufficient detail",
//     'clinician_has_clarified_end_of_life_prognosis': "Clarify end of life prognosis to the patient",
//     'patient_agreed_to_create_plan': "Get the patient to agree to creating a future health plan",
//     'importance_of_end_of_life_planning_discussed': "Emphasize the important of end of life planning",
//     'prognosis_progression_scenarios': "Describe the prognosis, progression, and health scenarios at end of life",
//     'incapacitaion_decision_making': "Describe how patient's may become incapacitated at end of life",
//     'options_care_pain_management': "Describe options for comfort care and pain management",
//     'decisions_healthcare_agent': "Describe decisions that a healthcare agent would make",
//     'discovery_of_whats_important_for_end_of_life_care': "Discover what is important for the patient in end of life care",
//     'discovery_of_past_experience_with_others_end_of_life': "Explore any past experience with others' end of life",
//     'counseled_on_where_to_gather_information': "Counsel patient on where to gather information",
//     'summarize_patients_concerns_and_perspective': "Summarize the conversation thus far",
//     'identify_healthcare_agent': "Identify patient's healthcare agent",
//     'identify_preference_for_life_sustaining_treatment': "Document's patient preferences for what life sustaining treatment they'd want at end of life",
//     'identify_preference_of_comfort_care_and_pain_management_options': "Document's patient preferences for pain management and comfort care",
//     'gather_preference_for_others_behavior': "Document's patient preferences for how they want loved ones to behave around them",
//     'identify_wishes_for_loved_ones': "Document's patient's wishes for their loved ones",
//     'two_reflections_used_in_response': "Use reflections to build rapport",
//     'two_open_questions_used_in_response': "Use open questions to learn more about the client",
//     'two_affirmations_used_in_response': "Use affirmations to encourage the client",
//     'client_shared_health_challenge': "Get the client to share their health challenge in detail",
//     'twelve_exchanges_complete': "Explore the client's challenges in sufficient detail",
//     'try_to_get_client_to_define_ideal_health': "Ask the client to visualize what their ideal health outcome would look like",
//     'client_shared_view_of_ideal_health': "Get the client to articulate what their ideal health would be",
//     'describe_how_cindy_could_change_behavior': "Describe your philosophy for how Cindy could change behaviors",
//     'emphasize_belief_in_clients_self_efficacy': "Emphasize your belief in the client's self efficacy",
//     'outline_how_when_client_coach_engagement': "Outline how and when you will engage with the client",
//     'describe_smart_goal_setting': "Describe SMART goal setting",
//     'redirect_to_goal_setting': "Redirect the patient towards setting goals",
//     'suggest_multiple_goals': "Suggest multiple goals to the patient",
//     'articulate_goal_in_smart_format': "Articulate the client's preferred goal in a SMART format",
//     'obtain_commitment_from_client': "Obtain commitment from the client on taking action on the goal"
//   };

// export function prettyPrintChecklist(input) {
//   const trimmedInput = input.trim().toLowerCase();
//   for (const [key, value] of Object.entries(keyValuePairs)) {
//     if (trimmedInput === key.toLowerCase()) {
//       return value;
//     }
//   }
//   return input;
// }
export interface Stage {
  title: string;
  subgoals: String[];
}

export interface Instructions {
  intro: string;
  stages: Stage[];
}
const healthCoachInstructions = {
  intro:
    "In this role play, you'll practice as a health coach having their first session with a client aiming to get healthier and lose weight. The client may stray off topic, so you'll need to use appropriate coaching techniques while empathetically guiding them through 4 stages and achieving the sub-goals within each.",
  stages: [
    {
      title: "Establish rapport",
      subgoals: [
        "Demonstrate presence through reflections",
        "Show interest with open questions",
        "Encourage with affirmations",
        "Get patient to share health challenges",
      ],
    } as Stage,
    {
      title: " Visualize better future",
      subgoals: [
        "Discuss ideal future health state",
        "Help overcome visualization impediments",
      ],
    } as Stage,
    {
      title: "Explain coaching process",
      subgoals: [
        "Describe behavior change philosophy",
        "Emphasize client's self-efficacy",
        "Outline interaction frequency",
        "Explain SMART goal setting",
      ],
    } as Stage,
    {
      title: "Set goals",
      subgoals: [
        "Redirect towards goal setting",
        "Propose three distinct goals",
        "Get commitment to one goal",
        "Frame goal in SMART format",
      ],
    } as Stage,
  ],
} as Instructions;
const ccmMaggieDay2 = {
  intro: "In this simulated exercise, you will act as a transition of care nurse following up with a recently discharged patient. Your primary goal is to assess the patient’s current condition, and address barriers to care. Begin by introducing yourself and establishing rapport with the patient, ensuring they feel supported in their transition home. Assess the patient’s current symptoms, medication adherence, and any immediate concerns. Identify gaps in care. Explore available resources to support medication adherence. Educate the patient on the importance of managing her chronic conditions and encourage follow-up appointments. Additionally, discuss lifestyle modifications, and assist in scheduling an appointment with a primary care provider.",
  stages: [
    {
      title: "Building Rapport, Current Symptoms, & Initial Medication Reconciliation",
      subgoals: [
        "Ensure proper patient identification and introduction",
        "Assess patient’s current health status and concerns",
        "Discuss care coordination and access to services",
        "Review and reconcile all medications",
      ],
    } as Stage,
    {
      title: "Uncovering Medical and Social Barriers",
      subgoals: [
        "Assess the patient’s financial concerns, including medication affordability and work-related challenges.",
        "Clarify the patient’s living situation, caregiving duties, and related stressors.",
        "Identify any missed or inconsistently taken prescriptions due to cost or side effects.",
        "Uncover social barriers affecting adherence to the care plan.",
      ],
    } as Stage,
    {
      title: "Providing Solutions and Exploring Patient Readiness",
      subgoals: [
        "Identify and suggest cost-saving options for prescriptions.",
        "Provide scheduling solutions to accommodate the patient’s needs.",
        "Address medication concerns and offer strategies to minimize disruptions.",
        "Discuss lifestyle modifications and assess the patient’s support needs.",
      ],
    } as Stage,
    {
      title: "Establishing a Care Plan and Securing Follow-Up",
      subgoals: [
        "Summarize the care plan, including next steps and follow-up actions.",
        "Clarify important symptoms to monitor and when to seek urgent care.",
        "Confirm the patient’s understanding, agreement, and access to support.",
        "Arrange follow-up to reassess progress and adjust the plan.",
      ],
    } as Stage,
  ],
};


const endOfLifeSimulation = {
  intro: "Your goal is to speak with a patient, John, about end of life planning. Show empathy as you educate and inform them, and ultimately get them to make certain decisions. Explore their challenges and guide them through four stages of counseling. To move forward, you'll need to complete the tasks in each stage's sub-goals. However, don't limit yourself to just these tasks, as you'll be evaluated on how well you follow best practices for end of life planning conversations. Here is the patient's medical history.",
  stages: [
    {
      title:
        "Initiating and framing the end-of-life planning conversation",
      subgoals: [
        "Introduce yourself and verify patient's identity",
        "Uncover patient's own understanding of their health related to all their conditions",
        "Clarify end of life prognosis to the patient",
        "Get the patient to agree to learning about end of life planning",
        "Emphasize the important of end of life planning",
        "Explore the client's challenges in sufficient detail",
      ],
    } as Stage,
    {
      title:
        "Providing information on end-of-life medical scenarios and care options",
      subgoals: [
        "Describe the prognosis, progression, and health scenarios at end of life",
        "Describe how a patient may become incapacitated at end of life",
        "Describe options for comfort care and pain management",
        "Describe decisions that a healthcare agent would make",
      ],
    } as Stage,
    {
      title: "Exploring patient values and expectations for end-of-life care",
      subgoals: [
        "Ask the patient about what they expect from their treatment and medical team",
        "Explore any past experience with others' end of life",
        "Counsel patient on where to gather information",
        "Discover what is important for the patient in end of life care",
      ],
    } as Stage,
    {
      title: "Documenting and finalizing patient preferences for end-of-life care",
      subgoals: [
        "Summarize the conversation thus far",
        "Identify patient's healthcare agent",
        "Document patient’s preferences for what life sustaining treatment they'd want at end of life",
        "Document patient’s preferences for pain management and comfort care",
        "Document patient’s preferences for how they want loved ones to behave around them",
        "Document patient's wishes for their loved ones",
      ],
    } as Stage,
  ],
};

const anesthesiaInformedConsentSimulation = {
  intro: "In this role play your goal is to discuss anesthesia options with a patient and counsel them with the ultimate aim of getting verbal consent. Show empathy as you explore their challenges and guide them through four stages. To move forward, you'll need to complete the tasks in each stage's sub-goals. However, don't limit yourself to just these tasks, as you'll be evaluated on how well you follow overall best practices for such a conversation. Here's the patient's medical history, and current setting. ",
  stages: [
    {
      title:
        "Introduction and gathering patient history",
      subgoals: [
        "Identify patient's primary fears",
        "Verify patient identity",
        "Encourage patient to share fears",
        "Introduce yourself",
        "Explain the goal of the conversation",
      ],
    } as Stage,
    {
      title:
        "Educate the patient on anesthesia options",
      subgoals: [
        "Ask about anesthesia fears",
        "Discuss benefits and risks of general anesthesia",
        "Discuss benefits and risks of regional anesthesia",
        "Relate general anesthesia benefits to patient concerns",
        "Relate regional anesthesia benefits to patient concerns",
        "Counter misconceptions with evidence-based explanations",

      ],
    } as Stage,
    {
      title: "Counsel patient on post-operative pain management techniques",
      subgoals: [
        "Ask about patient's concerns on postoperative pain",
        "Detail pain management options with regional anesthesia",
        "Detail pain management options with general anesthesia",
        "Describe medication-based pain management including opioids and non-opioids",
      ],
    } as Stage,
    {
      title: "Finalize the plan and get consent",
      subgoals: [
        "Present alternative strategies for anesthesia",
        "Outline contingency plan for anesthesia",
        "Summarize the final anesthesia plan",
        "Confirm patient understanding and obtain verbal consent",
      ],
    } as Stage,
  ],
};


const transitionToCareSimulation = {
  intro:
    "Your goal is to conduct a transitional care call with a patient who was recently discharged from the hospital. Show empathy as you explore their challenges and guide them through four stages of care. To move forward, you'll need to complete the tasks in each stage's sub-goals. However, don't limit yourself to just these tasks, as you'll be evaluated on how well you follow overall best practices for transition of care conversations. Here's the patient's medical history.",
  stages: [
    {
      title:
        "Introduction and Medical Review",
      subgoals: [
        "Greet the patient and verify identity.",
        "Explain the purpose of the call.",
        "Ask about the recent hospitalization and any complications.",
        "Inquire about current symptoms.",
      ],
    } as Stage,
    {
      title:
        "Health Status and Medication Review",
      subgoals: [
        "Ask about symptoms related to comorbidities.",
        "Review medications.",
        "Identify and address misconceptions about medication and diet.",
      ],
    } as Stage,
    {
      title: "Clarifications and Support Resources",
      subgoals: [
        "Clarify confusion about doctor’s guidance.",
        "Explain fluid intake recommendations.",
        "Stress the importance of not skipping medications or changing diet without consultation.",
        "Inquire about the need for community resources.",
        "Discuss DMEs.",
        "Discuss end-of-life planning.",
      ],
    } as Stage,
    {
      title: "Risk Management and Follow-up",
      subgoals: [
        "Identify risks for rehospitalization and strategies to prevent them.",
        "Set clear expectations for physician appointments.",
        "Summarize actions for medication management, follow-ups, and resources.",
        "Schedule follow-up appointments.",
      ],
    } as Stage,
  ],
};

const tocDay7SallyUpperGI = {
  intro:
    "Your task is to conduct a 7-day TOC call with the patient. Uncover all immediate medical and psychosocial concerns. Conduct a thorough medication review and take action to educate or escalate as appropriate. Create patient centered activities for the patient’s plan of care that target preventing a future admission. End the call with a summary of all your assessments, advice, and planning, i.e., anything that you would include in a SOAP note. \n You had a 2 day TOC call with them where you learnt that they had a PPI (proton pump inhibitor) added to their medication list and that they were feeling more fatigued than usual. They self-reported a post-discharge hemoglobin of 7. You educated them on dietary restrictions considering their GI bleed.\nPayer is IKC.",
  stages: [
    {
      title:
        "Introduction and Medical Review",
      subgoals: [
        "Introduce yourself and verify the patient’s identity.",
        "Assess symptoms and review medications for potential issues.",
        "Evaluate key lab data",
        "Conduct a comprehensive health assessment",
      ],
    } as Stage,
    {
      title: "Education",
      subgoals: [
        "Educate the patient on medication use and potential risks.",
        "Coordinate care regarding high-risk medication combinations.",
        "Review and update diet, medication, and care plans.",
        "Reinforce adherence to monitoring and lifestyle recommendations.",
      ],
    } as Stage,
    {
      title:
        "Provide support",
      subgoals: [
        "Confirm and facilitate scheduling of necessary follow-up appointments.",
        "Recommend alternatives for glucose monitoring and address patient concerns.",
        "Provide explanations for symptoms, suggest solutions, and propose topics for specialist consultation.",
        "Highlight critical health indicators and recommend ongoing monitoring.",
      ],
    } as Stage,
    {
      title: "Risk Management and Follow-up",
      subgoals: [
        "Communicate critical health priorities to the patient.",
        "Educate on GI bleeding signs and when to seek help.",
        "Confirm care plan agreement and arrange follow-up.",
        "Address fall prevention, low albumin, and high potassium management.",

      ],
    } as Stage,
  ],
};

const salesHealthTechMsk1 = {
  intro: "Your goal is to engage a prospective buyer from a Fortune 200 energy company in a conversation about a digital MSK solution. Demonstrate credibility, address their specific concerns, and clearly communicate the value of your offering in terms of clinical outcomes, engagement, and ROI. You’ll need to respond to objections, highlight differentiators, and guide the buyer through a consultative sales experience. While key topics may be prompted, your success depends on how well you follow best practices in enterprise solution selling.",
  stages: [
    {
      title: "Initiating the Conversation and Understanding Buyer Needs",
      subgoals: [
        "Introduce yourself, set the tone, and confirm the buyer’s preferences for the conversation style",
        "Ask the buyer to explain their motivation for exploring a virtual MSK solution",
        "Explore any previous experiences with MSK solutions",
        "Begin surfacing key buyer goals",

      ],
    } as Stage,
    {
      title: "Explaining the MSK Solution and Addressing Friction Points",
      subgoals: [
        "Clearly explain the core structure of your program, including PT-led care, continuity of treatment, and virtual engagement workflows.",
        "Address concerns about fragmented care, tech accessibility, or comparison to competitors",
        "Highlight differentiators of your model",
        "Explore opportunities for proactive care",
      ],
    } as Stage,

    {

      title: "Demonstrating Value and Tailoring the Solution",
      subgoals: [
        "Describe pricing and performance guarantees",
        "Share how your solution is customizable to energy-sector workforces",
        "Align the solution with the company’s internal communications and enrollment strategy.",
        "Frame how your solution supports broader goals",
      ],
    } as Stage,



    {

      title: "Finalizing the Value Proposition and Next Steps",
      subgoals: [
        "Reconfirm key takeaways and clarify any outstanding buyer questions",
        "Offer to provide follow-up documentation",
        "Secure next steps—such as a follow-up meeting, data submission, or pilot proposal discussion.",
      ],
    } as Stage,


  ]
}


const rubyConservativeManagement = {
  intro: "Mr. Anthony “Tony” H. is a 57-year-old African American man and a second-generation resident of Baltimore, currently living in the Sandtown-Winchester neighborhood of West Baltimore (ZIP 21217), a federally designated Health Professional Shortage Area (HPSA). He resides in a ground-floor two-bedroom row house with his spouse and adult nephew.",
  stages: [
    {
      title: "Building Rapport and Exploring Current Health Status",
      subgoals: [
        "Establish rapport and explain the purpose of the call in a patient-centered way",
        "Confirm discharge details and ask about any immediate health concerns",
        "Screen for concerning symptoms related to heart failure and kidney disease",
        "Assess mood and readiness to engage in a short follow-up conversation",
      ],
    } as Stage,
    {
      title: "Uncovering Medical and Social Barriers",
      subgoals: [
        "Review the medication list for duplications and gaps in access or adherence",
        "Confirm refill needs and check for barriers to getting or taking medications",
        "Discuss dietary habits and explore simple changes to support heart and kidney health",
        "Screen for substance use, financial strain, and interest in support resources",
      ],
    } as Stage,
    {
      title: "Providing Solutions and Exploring Patient Readiness",
      subgoals: [
        "Discuss the possibility of needing dialysis soon and the risks of delaying a decision",
        "Provide education on dialysis options and invite the patient (and spouse) to learn more",
        "Explore the patient’s concerns and feelings about dialysis, including past family experiences",
        "Clarify current treatment preference and introduce advance care planning if appropriate",
      ],
    } as Stage,
    {
      title: "Establishing a Care Plan and Securing Follow-Up",
      subgoals: [
        "Confirm medication updates and plan to communicate with the care team as needed",
        "Review next steps for labs, follow-up appointments, and ongoing monitoring",
        "Coordinate support services for transportation, vaccines, and financial needs",
        "Set expectations for patient follow-up, education participation, and the next check-in",
      ],
    } as Stage,
  ],
}
const rubyConservativeManagementFemale = {
  intro: "Mrs. Tanya H. is a 57-year-old African American woman and a second-generation resident of Baltimore, currently living in the Sandtown-Winchester neighborhood of West Baltimore (ZIP 21217), a federally designated Health Professional Shortage Area (HPSA). She resides in a ground-floor two-bedroom row house with her spouse and adult nephew.",
  stages: [
    {
      title: "Building Rapport and Exploring Current Health Status",
      subgoals: [
        "Establish rapport and explain the purpose of the call in a patient-centered way",
        "Confirm discharge details and ask about any immediate health concerns",
        "Screen for concerning symptoms related to heart failure and kidney disease",
        "Assess mood and readiness to engage in a short follow-up conversation",
      ],
    } as Stage,
    {
      title: "Uncovering Medical and Social Barriers",
      subgoals: [
        "Review the medication list for duplications and gaps in access or adherence",
        "Confirm refill needs and check for barriers to getting or taking medications",
        "Discuss dietary habits and explore simple changes to support heart and kidney health",
        "Screen for substance use, financial strain, and interest in support resources",
      ],
    } as Stage,
    {
      title: "Providing Solutions and Exploring Patient Readiness",
      subgoals: [
        "Discuss the possibility of needing dialysis soon and the risks of delaying a decision",
        "Provide education on dialysis options and invite the patient (and spouse) to learn more",
        "Explore the patient’s concerns and feelings about dialysis, including past family experiences",
        "Clarify current treatment preference and introduce advance care planning if appropriate",
      ],
    } as Stage,
    {
      title: "Establishing a Care Plan and Securing Follow-Up",
      subgoals: [
        "Confirm medication updates and plan to communicate with the care team as needed",
        "Review next steps for labs, follow-up appointments, and ongoing monitoring",
        "Coordinate support services for transportation, vaccines, and financial needs",
        "Set expectations for patient follow-up, education participation, and the next check-in",
      ],
    } as Stage,
  ],
}

const eolAssessment = {
  intro: "Your goal is to speak with a patient, John, about end of life planning. Show empathy as you educate and inform them, and ultimately get them to think about specific end of life planning decisions. Explore their challenges and guide them through a conversation using the goals below as a guide. Here is the patient's medical history.",
  stages: [
    {
      title: "Initiating and framing the end-of-life planning conversation",
      subgoals: [
        "Introduce yourself and verify patient's identity",
        "Uncover patient's own understanding of their health related to all their conditions",
        "Clarify end of life prognosis to the patient",
        "Get the patient to agree to learning about end of life planning",
        "Emphasize the important of end of life planning",
        "Explore the client's challenges in sufficient detail",
      ],
    } as Stage,


    {
      title: "Providing information on end-of-life medical scenarios and care options",
      subgoals: [
        "Describe the prognosis, progression, and health scenarios at end of life",
        "Describe how a patient may become incapacitated at end of life",
        "Describe options for comfort care and pain management",
        "Describe decisions that a healthcare agent would make",
      ],
    } as Stage,


    {
      title: "Exploring patient values and expectations for end-of-life care",
      subgoals: [
        "Ask the patient about what they expect from their treatment and medical team",
        "Explore any past experience with others' end of life",
        "Counsel patient on where to gather information",
        "Discover what is important for the patient in end of life care",
      ],
    } as Stage,


    {
      title: "Documenting and finalizing patient preferences for end-of-life care",
      subgoals: [
        "Summarize the conversation thus far",
        "Identify patient's healthcare agent",
        "Understand patient’s preferences for what life sustaining treatment they'd want at end of life",
        "Understand patient’s preferences for pain management and comfort care",
        "Understand patient’s preferences for how they want loved ones to behave around them",
        "Understand patient's wishes for their loved ones",
      ],
    } as Stage,
  ]
}

const ikcRenalAssessmentTransitionRisk = {
  intro: "",
  stages: []
}
const ikcExistingDialysisAssessment = {
  intro: "",
  stages: []
}

const tocSallyUpperGiLantus = {
  intro: "Your roster has changed and you have a new patient. Help them out.",
  stages: []
}

const ikcCareGiverConversation = {
  intro: "",
  stages: []
}

export const SIM_CONFIGS = {
  EOL: {
    voice: "en-US-DavisNeural",
    style: {
      "1": "sad",
      "2": "sad",
      "3": "sad",
      "4": "sad"
    },
    styleDegree: "1.2",
    role: "OlderAdultMale",
    instructions: endOfLifeSimulation
  },
  TRANSITION_OF_CARE_FINAL: {
    voice: "en-US-DavisNeural",
    style: { "1": "sad", "2": "sad", "3": "sad", "4": "sad" },
    styleDegree: "1.2",
    role: "OlderAdultMale",
    instructions: transitionToCareSimulation
  },
  NEW_EOL: {
    voice: "en-US-DavisNeural",
    style: { "1": "sad", "2": "sad", "3": "sad", "4": "sad" },
    styleDegree: "1.2",
    role: "OlderAdultMale",
    instructions: endOfLifeSimulation
  },
  HEALTH_COACHING: {
    voice: "en-US-MonicaNeural",
    style: { "1": "hopeful", "2": "hopeful", "3": "hopeful", "4": "hopeful" },
    styleDegree: "1.2",
    instructions: healthCoachInstructions
  },
  ANESTHESIA_INFORMED_CONSENT: {
    voice: "en-US-LolaMultilingualNeural",
    style: { "1": "hopeful", "2": "hopeful", "3": "hopeful", "4": "hopeful" },
    styleDegree: "1.2",
    instructions: anesthesiaInformedConsentSimulation
  },
  DEFAULT: {
    voice: "en-US-DavisNeural",
    style: { "1": "hopeful", "2": "hopeful", "3": "hopeful", "4": "hopeful" },
    styleDegree: "0.2",
    instructions: anesthesiaInformedConsentSimulation
  },
  TOC_DAY7_SALLY_UPPER_GI: {
    voice: "en-US-LolaMultilingualNeural",
    style: { "1": "hopeful", "2": "hopeful", "3": "hopeful", "4": "hopeful" },
    styleDegree: "1.2",
    instructions: tocDay7SallyUpperGI
  },
  TOC_DAY7_SALLY_UPPER_GI_ASSESSMENT: {
    voice: "en-US-LolaMultilingualNeural",
    style: { "1": "hopeful", "2": "hopeful", "3": "hopeful", "4": "hopeful" },
    styleDegree: "1.2",
    instructions: tocDay7SallyUpperGI
  },
  HEALTH_COACHING_ASSESSMENT: {
    voice: "en-US-MonicaNeural",
    style: { "1": "hopeful", "2": "hopeful", "3": "hopeful", "4": "hopeful" },
    styleDegree: "1.2",
    instructions: healthCoachInstructions
  },
  CCM_MAGGIE_DAY2: {
    voice: "en-PH-RosaNeural",
    style: { "1": "hopeful", "2": "hopeful", "3": "hopeful", "4": "hopeful" },
    styleDegree: "1.2",
    instructions: ccmMaggieDay2
  },
  CCM_MAGGIE_DAY2_ASSESSMENT: {
    voice: "en-US-SerenaMultilingualNeural",
    style: { "1": "hopeful", "2": "hopeful", "3": "hopeful", "4": "hopeful" },
    styleDegree: "1.2",
    instructions: ccmMaggieDay2
  },
  SALES_HEALTHTECH_MSK_1: {
    voice: "en-US-AndrewMultilingualNeural",
    style: { "1": "sad", "2": "sad", "3": "sad", "4": "sad" },
    styleDegree: "1.2",
    instructions: salesHealthTechMsk1
  },
  EOL_ASSESSMENT: {
    voice: "en-US-DavisNeural",
    style: { "1": "sad", "2": "sad", "3": "sad", "4": "sad" },
    styleDegree: "1.2",
    instructions: eolAssessment
  },
  RUBY_CONSERVATIVE_MANAGEMENT: {
    voice: "en-US-DavisNeural",
    style: { "1": "unfriendly", "2": "unfriendly", "3": "unfriendly", "4": "unfriendly" },
    styleDegree: "1.2",
    instructions: rubyConservativeManagement
  },
  TOC_SALLY_UPPER_GI_LANTUS: {
    voice: "en-US-SerenaMultilingualNeural",
    style: { "1": "shy", "2": "shy", "3": "shy", "4": "shy" },
    styleDegree: "1.2",
    instructions: tocSallyUpperGiLantus
  },
  IKC_RENAL_ASSESSMENT_TRANSITION_RISK: {
    voice: "en-US-SerenaMultilingualNeural",
    style: { "1": "sad", "2": "sad", "3": "sad", "4": "sad" },
    styleDegree: "1.2",
    instructions: ikcRenalAssessmentTransitionRisk
  },
  IKC_EXISTING_DIALYSIS_ASSESSMENT: {
    voice: "en-PH-RosaNeural",
    style: { "1": "shy", "2": "shy", "3": "shy", "4": "shy" },
    styleDegree: "1.2",
    instructions: ikcExistingDialysisAssessment
  },
  IKC_NEW_TO_DIALYSIS_ASSESSMENT: {
    voice: "en-US-DavisMultilingualNeural",
    style: { "1": "funny", "2": "funny", "3": "funny", "4": "funny" },
    styleDegree: "1.2",
    instructions: ikcExistingDialysisAssessment
  },
  IKC_CARE_GIVER_CONVERSATION: {
    voice: "en-US-NancyMultilingualNeural",
    style: { "1": "default", "2": "default", "3": "default", "4": "default" },
    styleDegree: "1.2",
    instructions: ikcCareGiverConversation
  },
  IKC_INFECTION_ASSESSMENT: {
    voice: "en-US-LolaMultilingualNeural",
    style: { "1": "default", "2": "default", "3": "default", "4": "default" },
    styleDegree: "1.2",
    instructions: ikcRenalAssessmentTransitionRisk
  },
  RUBY_CONSERVATIVE_MANAGEMENT_IMAGE_EMOTION_FEMALE: {
    voice: "en-US-SerenaMultilingualNeural",
    style: { "1": "shy", "2": "shy", "3": "shy", "4": "shy" },
    styleDegree: "1.2",
    instructions: rubyConservativeManagementFemale
  },
  RUBY_CONSERVATIVE_MANAGEMENT_IMAGE_EMOTION: {
    voice: "en-US-DavisNeural",
    style: { "1": "shy", "2": "shy", "3": "shy", "4": "shy" },
    styleDegree: "1.2",
    instructions: rubyConservativeManagement
  },
  DKC_PATIENT_END_DIALYSIS_EARLY: {
    voice: "en-US-AriaNeural",
    style: { "1": "shy", "2": "shy", "3": "shy", "4": "shy" },
    styleDegree: "1.2",
    instructions: ikcRenalAssessmentTransitionRisk
  }
};


export const FEEDBACK_KEYS = {
  cultivating_change_talk: "Demonstrating cultivating change talk",
  empathy: "Demonstrating empathy",
  voice_qa: "Style",
  partnership: "Demonstrating partnership",
  softening_sustain_talk: "Demonstrating softening sustain talk"
}

export const generateRandomNumber = (min: number, max: number) => {
  const randomNumber = Math.floor(Math.random() * (max - min + 1)) + min;
  console.log("random number", randomNumber);
  return randomNumber;
}

export const checkIfRandomNumberIsOne = () => {
  return generateRandomNumber(1, 3) === 1;
}

export const getMessageLimit = () => {
  return 200;
}

export const getExcuseMessage = () => {
  const messages = [
    "Sorry, I’ve got someone at the door—I have to hang up now.",
    "I’m really tired and need to lie down; can we stop here?",
    "Look, I’m overwhelmed and don’t want to discuss this anymore—good-bye.",
    "My phone’s about to die, so I need to end the call.",
    "Something important has come up; let’s just end this for today.",
  ];
  return messages[Math.floor(Math.random() * messages.length)];
}